import { useEffect, useRef, useState } from "react";
import useNetworkDeviceList from "./hooks/useNetworkDeviceList";
import axios from "axios";
import { Link } from "react-router-dom";
import fetchProfile from "../../../api/userProfile";
import sortedData from "../../../util/sortedData";
import FilterDropdown from "../../../components/atoms/filterDropdown/FilterDropdown";
import {
  CustomColumnProps,
  DynamicTable,
} from "../../../components/table/dynamicTable";

const NetworkDeviceList = () => {
  const {
    data,
    loading,
    currentPage,
    nextPage,
    count,
    handleDeleteClick,
    columns,
    tableData,
    setTableData,
    isFetchingComplete,
  } = useNetworkDeviceList(0);

  const [isFetching, setIsFetching] = useState(false);
  const [sortBy, setSortBy] = useState("networkDeviceId");
  const [sortDirection, setSortDirection] = useState("asc");
  const observer = useRef<IntersectionObserver | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    const profileData = await fetchProfile();
    setProfile(profileData);
  };

  useEffect(() => {
    if (!profile) return;
    if (loading || isFetchingComplete) return;

    const tableElement = document.getElementById("table");
    const scrollTriggerDivElement =
      document.getElementById("scroll-trigger-div");

    if (tableElement && scrollTriggerDivElement) {
      scrollTriggerDivElement.style.width = tableElement.clientWidth + "px";
    }

    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting) {
        loadMoreData();
      }
    };

    observer.current = new IntersectionObserver(handleObserver, {
      threshold: 0.2,
    });

    const scrollTriggerElement = document.getElementById("scroll-trigger");
    if (scrollTriggerElement) {
      observer.current.observe(scrollTriggerElement);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [loading, currentPage, isFetching, profile]);

  const loadMoreData = async () => {
    if (!isFetching) {
      setIsFetching(true);
      await nextPage();
      setIsFetching(false);
    }
  };

  const downloadExcel = async () => {
    alert("다운로드가 진행 중입니다.");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/network-device/download-excel`,
        {},
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "text/xlsx" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "네트워크_기기_테이블.xlsx");
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error creating xlsx:", error);
    }
  };

  const CustomEditButton: React.FC<{ item: any }> = ({ item }) => (
    <Link
      to={`/networkComponent/networkDevice/networkDeviceUpdate/${item.networkDeviceId}`}
    >
      수정
    </Link>
  );

  const CustomDeleteButton: React.FC<{ item: any }> = ({ item }) => (
    <button onClick={() => handleDeleteClick(item.networkDeviceId)}>
      삭제
    </button>
  );

  let customColumns: CustomColumnProps[] = [];

  if (
    tableData &&
    tableData.length > 0 &&
    Object.keys(tableData[0]).length > 0
  ) {
    customColumns = [
      {
        index: Object.keys(tableData[0]).length,
        rowText: "수정하기",
        component: CustomEditButton,
      },
      {
        index: Object.keys(tableData[0]).length + 1,
        rowText: "삭제하기",
        component: CustomDeleteButton,
      },
    ];
  }

  const handleReset = () => {
    setSortBy("networkDeviceId");
    setSortDirection("asc");
    setSearchTerm("");
    setStatusFilter("all");
  };

  return (
    <div className="container">
      <>
        <button className="excelButton" onClick={downloadExcel}>
          엑셀 다운로드
        </button>
        <div className="header">
          <h1>네트워크 기기 리스트</h1>
          <FilterDropdown
            columns={columns}
            searchTerm={searchTerm}
            tableData={tableData}
            allData={data}
            setTableData={setTableData}
          />
        </div>
        <div className="table-container">
          <DynamicTable data={tableData} customColumns={customColumns} />
          <div id="scroll-trigger-div">
            <div id="scroll-trigger" />
          </div>
        </div>
      </>
    </div>
  );
};

export default NetworkDeviceList;
